import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

export interface ThreadForProgramme {
  thread: {
    id: string
    title: string
    body: string
    group_id: string
  }
  group: {
    id: string
    name: string
    description: string | null
  }
}

/**
 * Finds an existing thread (and its group) for the given Channel+Programme identifier.
 * Used by the direct comment from TV flow: if a thread exists, offer "Add to existing chat?".
 *
 * Returns the first matching thread (multiple groups may have threads for the same programme;
 * we return one; user can choose "No, create New" to create in a different group).
 */
export function useThreadForProgramme(
  programmeIdentifier: string | null,
  channelId: string | null
) {
  const { user } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['thread-for-programme', programmeIdentifier, channelId],
    queryFn: async (): Promise<ThreadForProgramme | null> => {
      if (!programmeIdentifier || !user) return null

      const { data: rows, error: queryError } = await supabase
        .from('threads')
        .select(
          `
          id,
          title,
          body,
          group_id,
          groups (
            id,
            name,
            description
          )
        `
        )
        .eq('programme_identifier', programmeIdentifier)
        .eq('is_hidden', false)
        .limit(1)

      if (queryError) {
        // Columns may not exist yet if migration 018 not applied
        if (queryError.code === '42703') return null
        throw queryError
      }

      if (!rows || rows.length === 0) return null

      const row = rows[0] as {
        id: string
        title: string
        body: string
        group_id: string
        groups: unknown
      }
      const group = Array.isArray(row.groups) ? row.groups[0] : row.groups
      if (!group || typeof group !== 'object' || !('id' in group)) return null

      const g = group as { id: string; name: string; description: string | null }
      return {
        thread: {
          id: row.id,
          title: row.title,
          body: row.body,
          group_id: row.group_id,
        },
        group: {
          id: g.id,
          name: g.name,
          description: g.description ?? null,
        },
      }
    },
    enabled: !!programmeIdentifier && !!user,
    staleTime: 60000,
  })

  return { data: data ?? null, isLoading, error }
}
